<template>
  <div class="assessment-editor">
    <div class="editor-topbar">
      <button class="topbar-btn topbar-btn--ghost" type="button" @click="$emit('back')">
        ← 返回列表
      </button>
      <div class="topbar-actions">
        <button
          class="topbar-btn"
          :class="{ 'topbar-btn--danger': form.enabled }"
          type="button"
          :disabled="loading || saving || togglingEnabled"
          @click="toggleEnabled"
        >
          {{ togglingEnabled ? "提交中..." : form.enabled ? "禁用" : "启用" }}
        </button>
        <button
          class="topbar-btn"
          type="button"
          :disabled="loading"
          @click="showPreview = true"
        >
          预览
        </button>
        <button
          class="topbar-btn topbar-btn--primary"
          type="button"
          :disabled="loading || saving"
          @click="handleFinish"
        >
          {{ saving ? "保存中..." : "完成编辑" }}
        </button>
      </div>
    </div>

    <div v-if="loading" class="editor-loading">评估表加载中...</div>
    <div v-else class="editor-sheet">
      <div class="sheet-header">
        <input
          v-model.trim="form.title"
          class="sheet-title-input"
          placeholder="评估表标题"
        />
        <p class="sheet-subtitle">{{ form.subtitle }}</p>
      </div>

      <div
        v-for="(question, index) in form.questions"
        :key="question.id"
        class="question-card"
        :class="{ 'is-active': activeQuestionId === question.id }"
        @click="selectQuestion(question.id)"
      >
        <div class="question-drag-hint">⋮⋮</div>

        <!-- 浏览态 -->
        <template v-if="editingQuestionId !== question.id">
          <div class="question-view">
            <div class="question-title-row">
              <span v-if="question.required" class="required-star">*</span>
              <span class="question-index">{{ index + 1 }}.</span>
              <div class="question-content-text">{{ question.content || "（未填写题目内容）" }}</div>
            </div>

            <div v-if="question.type === 'matrix'" class="matrix-table-wrap">
              <table class="matrix-table">
                <thead>
                  <tr>
                    <th></th>
                    <th v-for="(col, cIdx) in question.columns" :key="'vh-col-' + cIdx">
                      {{ col || "列" + (cIdx + 1) }}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(row, rIdx) in question.rows" :key="'vh-row-' + rIdx">
                    <td class="matrix-row-label">{{ row || "行" + (rIdx + 1) }}</td>
                    <td
                      v-for="(col, cIdx) in question.columns"
                      :key="'vh-cell-' + rIdx + '-' + cIdx"
                      class="matrix-cell"
                    >
                      <span class="radio-dot"></span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div v-else class="single-options">
              <div
                v-for="(opt, oIdx) in question.options"
                :key="'vh-opt-' + oIdx"
                class="single-option-block"
              >
                <label class="single-option">
                  <span class="radio-dot"></span>
                  <span>{{ optionLabel(opt) || "选项" + (oIdx + 1) }}</span>
                </label>
                <div v-if="optionRemark(opt)" class="option-remark-hint">
                  备注：{{ optionRemark(opt) }}
                </div>
              </div>
            </div>

            <div
              v-if="question.type === 'matrix' && question.remarkRules && question.remarkRules.length"
              class="matrix-rules-view"
            >
              <div class="rules-title">结果备注规则</div>
              <div
                v-for="rule in question.remarkRules"
                :key="rule.id"
                class="rule-view-item"
              >
                当全部行选为「{{ question.columns[rule.columnIndex] || "列" + (rule.columnIndex + 1) }}」时：
                {{ rule.remark || "（未填写备注）" }}
              </div>
            </div>
          </div>

          <div class="question-toolbar">
            <button type="button" class="tool-link" @click.stop="startEditQuestion(question.id)">
              编辑
            </button>
            <button type="button" class="tool-link tool-link--danger" @click.stop="removeQuestion(index)">
              删除
            </button>
            <button type="button" class="tool-link" :disabled="index === 0" @click.stop="moveQuestion(index, -1)">
              上移
            </button>
            <button
              type="button"
              class="tool-link"
              :disabled="index === form.questions.length - 1"
              @click.stop="moveQuestion(index, 1)"
            >
              下移
            </button>
            <button type="button" class="tool-link" :disabled="index === 0" @click.stop="moveQuestionTo(index, 0)">
              最前
            </button>
            <button
              type="button"
              class="tool-link"
              :disabled="index === form.questions.length - 1"
              @click.stop="moveQuestionTo(index, form.questions.length - 1)"
            >
              最后
            </button>
          </div>

          <div class="insert-row">
            <button type="button" class="insert-link" @click.stop="openInsertMenu(index)">
              此题后插入新题
            </button>
            <div v-if="insertAfterIndex === index" class="insert-menu">
              <button type="button" @click.stop="insertQuestion(index, 'single')">常规单选</button>
              <button type="button" @click.stop="insertQuestion(index, 'matrix')">矩阵单选</button>
            </div>
          </div>
        </template>

        <!-- 编辑态 -->
        <template v-else>
          <button class="question-delete-icon" type="button" title="删除本题" @click.stop="removeQuestion(index)">
            ×
          </button>

          <div class="edit-block">
            <div class="edit-title-row">
              <span v-if="draft.required" class="required-star">*</span>
              <span class="question-index">{{ index + 1 }}.</span>
              <textarea
                v-model="draft.content"
                class="content-textarea"
                rows="6"
                placeholder="请输入检查内容与评判标准"
              ></textarea>
            </div>

            <!-- 矩阵编辑 -->
            <div v-if="draft.type === 'matrix'" class="matrix-edit">
              <table class="matrix-table matrix-table--edit">
                <thead>
                  <tr>
                    <th></th>
                    <th v-for="(col, cIdx) in draft.columns" :key="'ed-col-' + cIdx">
                      <div class="matrix-input-wrap">
                        <input v-model="draft.columns[cIdx]" class="matrix-input" />
                        <button
                          type="button"
                          class="mini-x"
                          title="删除列"
                          @click="removeColumn(cIdx)"
                        >
                          ×
                        </button>
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(row, rIdx) in draft.rows" :key="'ed-row-' + rIdx">
                    <td>
                      <div class="matrix-input-wrap">
                        <input v-model="draft.rows[rIdx]" class="matrix-input" />
                        <button
                          type="button"
                          class="mini-x"
                          title="删除行"
                          @click="removeRow(rIdx)"
                        >
                          ×
                        </button>
                      </div>
                    </td>
                    <td
                      v-for="(col, cIdx) in draft.columns"
                      :key="'ed-cell-' + rIdx + '-' + cIdx"
                      class="matrix-cell"
                    >
                      <span class="radio-dot"></span>
                    </td>
                  </tr>
                </tbody>
              </table>
              <div class="matrix-add-actions">
                <button type="button" class="add-link" @click="addRow">+ 添加一行</button>
                <button type="button" class="add-link" @click="addColumn">+ 添加一列</button>
              </div>

              <div class="matrix-rules-edit">
                <div class="rules-title">结果备注规则（全部行选完后匹配）</div>
                <div
                  v-for="(rule, ruleIdx) in draft.remarkRules"
                  :key="rule.id"
                  class="rule-edit-row"
                >
                  <span class="rule-text">当全部行选为</span>
                  <select v-model.number="rule.columnIndex" class="rule-select">
                    <option
                      v-for="(col, cIdx) in draft.columns"
                      :key="'rule-col-' + cIdx"
                      :value="cIdx"
                    >
                      {{ col || "列" + (cIdx + 1) }}
                    </option>
                  </select>
                  <span class="rule-text">时，提示</span>
                  <input
                    v-model="rule.remark"
                    class="rule-remark-input"
                  />
                  <button
                    type="button"
                    class="mini-x"
                    title="删除规则"
                    @click="removeRemarkRule(ruleIdx)"
                  >
                    ×
                  </button>
                </div>
                <button type="button" class="add-link" @click="addRemarkRule">
                  + 添加规则
                </button>
              </div>
            </div>

            <!-- 单选编辑 -->
            <div v-else class="single-edit">
              <div
                v-for="(opt, oIdx) in draft.options"
                :key="'ed-opt-' + oIdx"
                class="single-edit-block"
              >
                <div class="single-edit-row">
                  <span class="radio-dot"></span>
                  <input
                    v-model="opt.label"
                    class="option-input"
                    placeholder="选项文案"
                  />
                  <button type="button" class="mini-x" title="删除选项" @click="removeOption(oIdx)">
                    ×
                  </button>
                </div>
                <div class="option-remark-row">
                  <span class="remark-label">备注</span>
                  <input
                    v-model="opt.remark"
                    class="option-remark-input"
                    placeholder="选中该选项后显示的提示（可空）"
                  />
                </div>
              </div>
              <button type="button" class="add-link" @click="addOption">+ 添加选项</button>
            </div>
          </div>

          <div class="edit-footer">
            <label class="required-check">
              <input v-model="draft.required" type="checkbox" />
              必答
            </label>
            <select v-model="draft.type" class="type-select" @change="onDraftTypeChange">
              <option value="single">常规单选</option>
              <option value="matrix">矩阵单选</option>
            </select>
            <button type="button" class="cancel-edit-btn" @click="cancelEditQuestion">
              取消编辑
            </button>
            <button type="button" class="finish-edit-btn" @click="finishEditQuestion">
              完成编辑
            </button>
          </div>
        </template>
      </div>

      <div v-if="!form.questions.length" class="empty-questions">
        暂无题目，
        <button type="button" class="add-link" @click="insertQuestion(-1, 'single')">
          添加常规单选
        </button>
        或
        <button type="button" class="add-link" @click="insertQuestion(-1, 'matrix')">
          添加矩阵单选
        </button>
      </div>

      <div class="sheet-notes">
        <textarea
          v-model="form.notes"
          class="notes-textarea"
          rows="8"
          placeholder="表尾说明 / 禁忌证 / 风险告知（可选）"
        ></textarea>
      </div>
    </div>

    <!-- 同页预览 -->
    <div v-if="showPreview" class="preview-overlay">
      <div class="preview-topbar">
        <span class="preview-warn">提示：此为预览页面，不参与填写数据</span>
        <div class="preview-mode-tabs">
          <button
            type="button"
            class="mode-tab"
            :class="{ active: previewMode === 'mobile' }"
            @click="previewMode = 'mobile'"
          >
            手机预览
          </button>
          <button
            type="button"
            class="mode-tab"
            :class="{ active: previewMode === 'desktop' }"
            @click="previewMode = 'desktop'"
          >
            电脑预览
          </button>
          <button type="button" class="mode-tab" @click="showPreview = false">
            关闭预览
          </button>
        </div>
      </div>

      <div class="preview-body">
        <div class="preview-sheet" :class="{ 'is-mobile': previewMode === 'mobile' }">
          <div class="preview-banner"></div>
          <h2 class="preview-title">{{ form.title || "术前信息评估表" }}</h2>
          <p class="preview-subtitle">{{ form.subtitle }}</p>
          <div class="preview-divider"></div>

          <div
            v-for="(question, index) in form.questions"
            :key="'pv-' + question.id"
            class="preview-question"
          >
            <div class="question-title-row">
              <span v-if="question.required" class="required-star">*</span>
              <span class="question-index">{{ index + 1 }}</span>
              <div class="question-content-text">{{ question.content }}</div>
            </div>

            <div v-if="question.type === 'matrix'" class="matrix-table-wrap">
              <table class="matrix-table">
                <thead>
                  <tr>
                    <th></th>
                    <th v-for="(col, cIdx) in question.columns" :key="'pv-col-' + cIdx">
                      {{ col }}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(row, rIdx) in question.rows" :key="'pv-row-' + rIdx">
                    <td class="matrix-row-label">{{ row }}</td>
                    <td
                      v-for="(col, cIdx) in question.columns"
                      :key="'pv-cell-' + rIdx + '-' + cIdx"
                      class="matrix-cell"
                    >
                      <input
                        type="radio"
                        :name="'pv-matrix-' + question.id + '-' + rIdx"
                        :checked="previewAnswers[question.id] && previewAnswers[question.id][rIdx] === cIdx"
                        @change="setMatrixAnswer(question.id, rIdx, cIdx)"
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
              <div
                v-if="getPreviewMatrixRemarks(question).length"
                class="preview-remark-box"
              >
                <div
                  v-for="(text, tIdx) in getPreviewMatrixRemarks(question)"
                  :key="'pv-m-remark-' + tIdx"
                >
                  {{ text }}
                </div>
              </div>
            </div>

            <div v-else class="single-options">
              <div
                v-for="(opt, oIdx) in question.options"
                :key="'pv-opt-' + oIdx"
                class="single-option-block"
              >
                <label class="single-option">
                  <input
                    type="radio"
                    :name="'pv-single-' + question.id"
                    :checked="previewAnswers[question.id] === oIdx"
                    @change="setSingleAnswer(question.id, oIdx)"
                  />
                  <span>{{ optionLabel(opt) }}</span>
                </label>
              </div>
              <div
                v-if="getPreviewSingleRemark(question)"
                class="preview-remark-box"
              >
                {{ getPreviewSingleRemark(question) }}
              </div>
            </div>
          </div>

          <pre v-if="form.notes" class="preview-notes">{{ form.notes }}</pre>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  getAssessmentByProjectId,
  saveAssessment,
  setAssessmentEnabled,
} from "@/api/assessment";
import {
  ASSESSMENT_SUBTITLE,
  createEmptyQuestion,
  createOption,
  createRemarkRule,
  getOptionLabel,
  getOptionRemark,
  normalizeForm,
  normalizeQuestion,
  resolveMatrixRemarks,
  resolveSingleRemark,
  toSavePayload,
} from "@/utils/assessmentForm";

const SUCCESS_CODE = 20000;

function cloneQuestion(question) {
  return JSON.parse(JSON.stringify(question));
}

export default {
  name: "AssessmentFormEditor",
  props: {
    project: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      form: {
        projectId: "",
        projectName: "",
        title: "",
        subtitle: ASSESSMENT_SUBTITLE,
        notes: "",
        enabled: false,
        questions: [],
      },
      activeQuestionId: "",
      editingQuestionId: "",
      draft: null,
      insertAfterIndex: null,
      showPreview: false,
      previewMode: "desktop",
      previewAnswers: {},
      loading: false,
      saving: false,
      togglingEnabled: false,
    };
  },
  watch: {
    project: {
      immediate: true,
      handler(val) {
        this.loadFormData(val);
      },
    },
  },
  methods: {
    showMessage(type, text) {
      this.$message({ message: text, type });
    },
    optionLabel(opt) {
      return getOptionLabel(opt);
    },
    optionRemark(opt) {
      return getOptionRemark(opt);
    },
    isBizError(res) {
      return res && res.code != null && Number(res.code) !== SUCCESS_CODE;
    },
    applyForm(data, project) {
      const form = normalizeForm(data, project || {});
      this.form = {
        projectId: form.projectId,
        projectName: form.projectName,
        title: form.title || "术前信息评估表",
        subtitle: ASSESSMENT_SUBTITLE,
        notes: form.notes || "",
        enabled: form.enabled === true,
        questions: Array.isArray(form.questions)
          ? form.questions.map((q) => normalizeQuestion(q))
          : [],
      };
      this.activeQuestionId = "";
      this.editingQuestionId = "";
      this.draft = null;
      this.insertAfterIndex = null;
      this.showPreview = false;
      this.previewAnswers = {};
    },
    async loadFormData(project) {
      const current = project || {};
      const projectId = String(current.projectId || current.id || "").trim();
      this.applyForm(null, current);
      if (!projectId) return;
      this.loading = true;
      try {
        const res = await getAssessmentByProjectId({ projectId });
        if (this.isBizError(res)) {
          this.showMessage("error", (res && (res.msg || res.message)) || "评估表查询失败");
          return;
        }
        this.applyForm(res && res.data, current);
      } catch (error) {
        this.showMessage("error", error.message || "评估表查询失败");
      } finally {
        this.loading = false;
      }
    },
    selectQuestion(id) {
      if (this.editingQuestionId && this.editingQuestionId !== id) return;
      this.activeQuestionId = id;
      this.insertAfterIndex = null;
    },
    startEditQuestion(id) {
      const question = this.form.questions.find((item) => item.id === id);
      if (!question) return;
      this.activeQuestionId = id;
      this.editingQuestionId = id;
      this.draft = normalizeQuestion(cloneQuestion(question));
      this.insertAfterIndex = null;
    },
    finishEditQuestion() {
      if (!this.draft || !this.editingQuestionId) return;
      const idx = this.form.questions.findIndex(
        (item) => item.id === this.editingQuestionId
      );
      if (idx < 0) return;

      const next = normalizeQuestion(cloneQuestion(this.draft));
      if (next.type === "matrix") {
        if (!next.columns.length) next.columns = ["良", "中", "差"];
        if (!next.rows.length) next.rows = ["选项1"];
        next.remarkRules = (next.remarkRules || [])
          .map((rule) => ({
            ...rule,
            columnIndex: Math.min(
              Math.max(0, Number(rule.columnIndex) || 0),
              Math.max(0, next.columns.length - 1)
            ),
            remark: String(rule.remark || "").trim(),
          }))
          .filter((rule) => rule.remark);
      } else {
        next.options = (next.options || [])
          .map((opt) => createOption(opt.label, opt.remark))
          .filter((opt) => opt.label);
        if (!next.options.length) {
          next.options = [createOption("选项A"), createOption("选项B")];
        }
      }

      this.$set(this.form.questions, idx, next);
      this.editingQuestionId = "";
      this.draft = null;
    },
    cancelEditQuestion() {
      this.editingQuestionId = "";
      this.draft = null;
    },
    onDraftTypeChange() {
      if (!this.draft) return;
      const next = normalizeQuestion({
        ...this.draft,
        type: this.draft.type,
        options:
          this.draft.type === "single"
            ? this.draft.options && this.draft.options.length
              ? this.draft.options
              : [createOption("选项A"), createOption("选项B")]
            : [],
        columns:
          this.draft.type === "matrix"
            ? this.draft.columns && this.draft.columns.length
              ? this.draft.columns
              : ["良", "中", "差"]
            : [],
        rows:
          this.draft.type === "matrix"
            ? this.draft.rows && this.draft.rows.length
              ? this.draft.rows
              : ["选项1", "选项2"]
            : [],
        remarkRules:
          this.draft.type === "matrix" ? this.draft.remarkRules || [] : [],
      });
      this.draft = next;
    },
    openInsertMenu(index) {
      this.insertAfterIndex = this.insertAfterIndex === index ? null : index;
    },
    insertQuestion(afterIndex, type) {
      const question = createEmptyQuestion(type);
      const insertAt = afterIndex + 1;
      this.form.questions.splice(insertAt, 0, question);
      this.insertAfterIndex = null;
      this.startEditQuestion(question.id);
    },
    removeQuestion(index) {
      const target = this.form.questions[index];
      if (!target) return;
      this.$confirm("确认删除该题目？", "提示", {
        type: "warning",
        confirmButtonText: "删除",
        cancelButtonText: "取消",
      })
        .then(() => {
          this.form.questions.splice(index, 1);
          if (this.editingQuestionId === target.id) {
            this.editingQuestionId = "";
            this.draft = null;
          }
          if (this.activeQuestionId === target.id) {
            this.activeQuestionId = "";
          }
        })
        .catch(() => {});
    },
    moveQuestion(index, delta) {
      const next = index + delta;
      if (next < 0 || next >= this.form.questions.length) return;
      const list = this.form.questions;
      const temp = list[index];
      this.$set(list, index, list[next]);
      this.$set(list, next, temp);
    },
    moveQuestionTo(index, targetIndex) {
      if (index === targetIndex) return;
      const list = this.form.questions;
      const [item] = list.splice(index, 1);
      list.splice(targetIndex, 0, item);
    },
    addRow() {
      if (!this.draft) return;
      this.draft.rows.push(`选项${this.draft.rows.length + 1}`);
    },
    removeRow(index) {
      if (!this.draft || this.draft.rows.length <= 1) {
        this.showMessage("warning", "至少保留一行");
        return;
      }
      this.draft.rows.splice(index, 1);
    },
    addColumn() {
      if (!this.draft) return;
      this.draft.columns.push(`列${this.draft.columns.length + 1}`);
    },
    removeColumn(index) {
      if (!this.draft || this.draft.columns.length <= 1) {
        this.showMessage("warning", "至少保留一列");
        return;
      }
      this.draft.columns.splice(index, 1);
      (this.draft.remarkRules || []).forEach((rule) => {
        if (rule.columnIndex >= this.draft.columns.length) {
          rule.columnIndex = this.draft.columns.length - 1;
        }
      });
    },
    addOption() {
      if (!this.draft) return;
      this.draft.options.push(
        createOption(`选项${this.draft.options.length + 1}`)
      );
    },
    removeOption(index) {
      if (!this.draft || this.draft.options.length <= 1) {
        this.showMessage("warning", "至少保留一个选项");
        return;
      }
      this.draft.options.splice(index, 1);
    },
    addRemarkRule() {
      if (!this.draft) return;
      if (!Array.isArray(this.draft.remarkRules)) {
        this.$set(this.draft, "remarkRules", []);
      }
      const defaultCol = Math.max(0, (this.draft.columns || []).length - 1);
      this.draft.remarkRules.push(createRemarkRule(defaultCol, ""));
    },
    removeRemarkRule(index) {
      if (!this.draft || !this.draft.remarkRules) return;
      this.draft.remarkRules.splice(index, 1);
    },
    setSingleAnswer(questionId, optionIndex) {
      this.$set(this.previewAnswers, questionId, optionIndex);
    },
    setMatrixAnswer(questionId, rowIndex, colIndex) {
      const current = this.previewAnswers[questionId] || {};
      this.$set(this.previewAnswers, questionId, {
        ...current,
        [rowIndex]: colIndex,
      });
    },
    getPreviewSingleRemark(question) {
      return resolveSingleRemark(question, this.previewAnswers[question.id]);
    },
    getPreviewMatrixRemarks(question) {
      return resolveMatrixRemarks(question, this.previewAnswers[question.id]);
    },
    async toggleEnabled() {
      const projectId = String(this.form.projectId || "").trim();
      if (!projectId) {
        this.showMessage("warning", "缺少服务项目，无法切换状态");
        return;
      }
      const nextEnabled = !this.form.enabled;
      this.togglingEnabled = true;
      try {
        const res = await setAssessmentEnabled({
          projectId,
          enabled: nextEnabled,
        });
        if (this.isBizError(res)) {
          this.showMessage(
            "error",
            (res && (res.msg || res.message)) || "状态更新失败"
          );
          return;
        }
        this.form.enabled = nextEnabled;
        this.showMessage(
          "success",
          nextEnabled
            ? "已启用：小程序端需填写该评估表"
            : "已禁用：小程序端无需填写该评估表"
        );
      } catch (error) {
        this.showMessage("error", error.message || "状态更新失败");
      } finally {
        this.togglingEnabled = false;
      }
    },
    async handleFinish() {
      if (this.editingQuestionId) {
        this.finishEditQuestion();
      }
      const payload = toSavePayload(this.form);
      if (!payload.projectId) {
        this.showMessage("warning", "缺少服务项目，无法保存");
        return;
      }
      this.saving = true;
      try {
        const res = await saveAssessment(payload);
        if (this.isBizError(res)) {
          this.showMessage(
            "error",
            (res && (res.msg || res.message)) || "保存失败"
          );
          return;
        }
        this.showMessage("success", "评估表已保存");
        this.$emit("saved", this.form);
      } catch (error) {
        this.showMessage("error", error.message || "保存失败");
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.assessment-editor {
  min-height: 100%;
  background: #f5f7fa;
  margin: 0;
  padding: 0 0 40px;
  box-sizing: border-box;
}

.editor-topbar {
  position: sticky;
  top: 0;
  z-index: 30;
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 56px;
  padding: 0 20px;
  background: #fff;
  border-bottom: 1px solid #e8e8e8;
}

.topbar-actions {
  display: flex;
  gap: 10px;
}

.topbar-btn {
  height: 32px;
  padding: 0 14px;
  border-radius: 4px;
  border: 1px solid #d9d9d9;
  background: #fff;
  color: #333;
  font-size: 14px;
  cursor: pointer;
}

.topbar-btn--ghost {
  border: none;
  color: #1890ff;
}

.topbar-btn--primary {
  background: #1890ff;
  border-color: #1890ff;
  color: #fff;
}

.topbar-btn--danger {
  border-color: #ffccc7;
  background: #fff1f0;
  color: #cf1322;
}

.topbar-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.editor-loading {
  padding: 80px 20px;
  text-align: center;
  color: #999;
  font-size: 14px;
}

.editor-sheet {
  width: 760px;
  max-width: calc(100% - 32px);
  margin: 24px auto 0;
  background: #fff;
  border-radius: 4px;
  border: 1px solid #eef1f6;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);
  padding: 28px 32px 40px;
}

.sheet-header {
  text-align: center;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px dashed #d9d9d9;
}

.sheet-title-input {
  width: 100%;
  border: none;
  outline: none;
  text-align: center;
  font-size: 26px;
  font-weight: 700;
  color: #1890ff;
  margin-bottom: 8px;
  background: transparent;
}

.sheet-subtitle {
  margin: 0;
  text-align: center;
  font-size: 13px;
  color: #8c8c8c;
  line-height: 1.5;
}

.question-card {
  position: relative;
  margin-bottom: 20px;
  padding: 16px 12px 12px;
  border: 1px solid transparent;
  border-radius: 4px;
}

.question-card.is-active {
  border: 1px dashed #91d5ff;
  background: #fafcff;
}

.question-drag-hint {
  text-align: center;
  color: #bfbfbf;
  font-size: 12px;
  margin-bottom: 6px;
  letter-spacing: 2px;
}

.question-delete-icon {
  position: absolute;
  top: 8px;
  right: 10px;
  border: none;
  background: transparent;
  color: #999;
  font-size: 22px;
  cursor: pointer;
  line-height: 1;
}

.question-title-row {
  display: flex;
  align-items: flex-start;
  gap: 4px;
  margin-bottom: 12px;
}

.required-star {
  color: #f5222d;
  line-height: 1.6;
}

.question-index {
  font-weight: 600;
  color: #333;
  line-height: 1.6;
  white-space: nowrap;
}

.question-content-text {
  white-space: pre-wrap;
  color: #333;
  font-size: 14px;
  line-height: 1.7;
  flex: 1;
}

.matrix-table-wrap {
  overflow-x: auto;
}

.matrix-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
}

.matrix-table th,
.matrix-table td {
  border: 1px solid #e8e8e8;
  padding: 10px 8px;
  text-align: center;
  font-size: 13px;
  color: #333;
  background: #fff;
}

.matrix-row-label {
  text-align: left !important;
  width: 140px;
}

.matrix-cell {
  width: 72px;
}

.radio-dot {
  display: inline-block;
  width: 14px;
  height: 14px;
  border: 1px solid #bfbfbf;
  border-radius: 50%;
  vertical-align: middle;
  background: #fff;
}

.single-options {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding-left: 8px;
}

.single-option-block {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.single-option {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #333;
}

.option-remark-hint {
  margin-left: 22px;
  font-size: 12px;
  color: #8c8c8c;
  line-height: 1.4;
}

.matrix-rules-view {
  margin-top: 12px;
  padding: 10px 12px;
  background: #fafafa;
  border: 1px dashed #e8e8e8;
  border-radius: 4px;
}

.rules-title {
  font-size: 13px;
  font-weight: 600;
  color: #595959;
  margin-bottom: 8px;
}

.rule-view-item {
  font-size: 13px;
  color: #595959;
  line-height: 1.5;
  margin-bottom: 4px;
}

.rule-view-item:last-child {
  margin-bottom: 0;
}

.question-toolbar {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 14px;
  padding-top: 10px;
  border-top: 1px solid #f0f0f0;
}

.tool-link {
  border: none;
  background: transparent;
  color: #595959;
  font-size: 13px;
  cursor: pointer;
  padding: 0;
}

.tool-link:hover:not(:disabled) {
  color: #1890ff;
}

.tool-link--danger:hover:not(:disabled) {
  color: #f5222d;
}

.tool-link:disabled {
  color: #d9d9d9;
  cursor: not-allowed;
}

.insert-row {
  margin-top: 10px;
  position: relative;
}

.insert-link,
.add-link {
  border: none;
  background: transparent;
  color: #1890ff;
  font-size: 13px;
  cursor: pointer;
  padding: 0;
}

.insert-menu {
  display: inline-flex;
  gap: 8px;
  margin-left: 10px;
}

.insert-menu button {
  height: 28px;
  padding: 0 10px;
  border: 1px solid #91d5ff;
  background: #e6f7ff;
  color: #1890ff;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
}

.edit-title-row {
  display: flex;
  align-items: flex-start;
  gap: 4px;
  margin-bottom: 12px;
}

.content-textarea {
  flex: 1;
  min-width: 0;
  width: 100%;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  padding: 8px 10px;
  font-size: 14px;
  line-height: 1.6;
  resize: vertical;
  box-sizing: border-box;
  color: #333;
}

.matrix-edit {
  margin-top: 8px;
}

.matrix-table--edit th,
.matrix-table--edit td {
  padding: 8px;
}

.matrix-input-wrap {
  display: flex;
  align-items: center;
  gap: 4px;
}

.matrix-input,
.option-input {
  width: 100%;
  height: 32px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  padding: 0 8px;
  font-size: 13px;
  box-sizing: border-box;
}

.mini-x {
  border: none;
  background: transparent;
  color: #999;
  cursor: pointer;
  font-size: 16px;
  line-height: 1;
  flex-shrink: 0;
}

.matrix-add-actions {
  display: flex;
  justify-content: space-between;
  margin-top: 10px;
}

.matrix-rules-edit {
  margin-top: 14px;
  padding: 12px;
  background: #fafcff;
  border: 1px solid #e6f4ff;
  border-radius: 4px;
}

.rule-edit-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 10px;
}

.rule-text {
  font-size: 13px;
  color: #595959;
  white-space: nowrap;
}

.rule-select {
  height: 32px;
  min-width: 88px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  padding: 0 8px;
  font-size: 13px;
}

.rule-remark-input {
  flex: 1;
  min-width: 180px;
  height: 32px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  padding: 0 8px;
  font-size: 13px;
  box-sizing: border-box;
}

.single-edit {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 8px;
}

.single-edit-block {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 10px;
  border: 1px solid #f0f0f0;
  border-radius: 4px;
  background: #fafafa;
}

.single-edit-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.option-remark-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding-left: 22px;
}

.remark-label {
  font-size: 12px;
  color: #8c8c8c;
  white-space: nowrap;
}

.option-remark-input {
  flex: 1;
  height: 30px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  padding: 0 8px;
  font-size: 12px;
  box-sizing: border-box;
}

.edit-footer {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 16px;
  padding-top: 12px;
  border-top: 1px solid #f0f0f0;
}

.required-check {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #333;
}

.type-select {
  height: 32px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  padding: 0 8px;
  font-size: 13px;
}

.cancel-edit-btn {
  margin-left: auto;
  height: 32px;
  padding: 0 16px;
  border-radius: 4px;
  background: #fff;
  color: #333;
  border: 1px solid #d9d9d9;
  cursor: pointer;
  font-size: 14px;
}

.cancel-edit-btn:hover {
  color: #1890ff;
  border-color: #1890ff;
}

.finish-edit-btn {
  height: 32px;
  padding: 0 16px;
  border: none;
  border-radius: 4px;
  background: #1890ff;
  color: #fff;
  cursor: pointer;
  font-size: 14px;
}

.empty-questions {
  text-align: center;
  color: #999;
  padding: 40px 0;
  font-size: 14px;
}

.sheet-notes {
  margin-top: 24px;
  border-top: 1px dashed #d9d9d9;
  padding-top: 16px;
}

.notes-textarea {
  width: 100%;
  border: 1px solid #f0f0f0;
  border-radius: 4px;
  padding: 10px;
  font-size: 13px;
  line-height: 1.6;
  color: #595959;
  resize: vertical;
  box-sizing: border-box;
  background: #fafafa;
}

.sheet-notes-add {
  margin-top: 16px;
}

.preview-overlay {
  position: fixed;
  inset: 0;
  z-index: 2000;
  background: linear-gradient(180deg, #d6e4ff 0%, #f0f5ff 40%, #f9f0ff 100%);
  display: flex;
  flex-direction: column;
}

.preview-topbar {
  height: 52px;
  background: #fff;
  border-bottom: 1px solid #e8e8e8;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 20px;
  flex-shrink: 0;
}

.preview-warn {
  color: #f5222d;
  font-size: 13px;
}

.preview-mode-tabs {
  display: flex;
  gap: 8px;
}

.mode-tab {
  height: 30px;
  padding: 0 12px;
  border: 1px solid #d9d9d9;
  background: #fff;
  border-radius: 4px;
  cursor: pointer;
  font-size: 13px;
  color: #333;
}

.mode-tab.active {
  background: #1890ff;
  border-color: #1890ff;
  color: #fff;
}

.preview-body {
  flex: 1;
  overflow: auto;
  padding: 24px 16px 40px;
}

.preview-sheet {
  width: 720px;
  max-width: 100%;
  margin: 0 auto;
  background: #fff;
  border-radius: 4px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
  padding: 0 28px 36px;
}

.preview-sheet.is-mobile {
  width: 390px;
}

.preview-banner {
  height: 72px;
  margin: 0 -28px 16px;
  background: linear-gradient(90deg, #bae7ff, #e6f7ff);
  border-radius: 4px 4px 0 0;
}

.preview-title {
  margin: 0 0 8px;
  text-align: center;
  color: #1890ff;
  font-size: 24px;
}

.preview-subtitle {
  margin: 0 0 16px;
  text-align: center;
  color: #8c8c8c;
  font-size: 13px;
}

.preview-divider {
  border-top: 1px dashed #d9d9d9;
  margin-bottom: 20px;
}

.preview-question {
  margin-bottom: 24px;
}

.preview-remark-box {
  margin-top: 10px;
  padding: 10px 12px;
  background: #fff7e6;
  border: 1px solid #ffd591;
  border-radius: 4px;
  color: #ad6800;
  font-size: 13px;
  line-height: 1.6;
}

.preview-notes {
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px dashed #d9d9d9;
  white-space: pre-wrap;
  font-family: inherit;
  font-size: 12px;
  color: #8c8c8c;
  line-height: 1.7;
}

.single-options input[type="radio"],
.matrix-table input[type="radio"] {
  margin: 0;
  cursor: pointer;
}
</style>
